/**
 * ContactListView.tsx - List-based contact display
 * 
 * CHANGE HISTORY:
 * - 251031_1725 [pending] danielbeer: Initial creation for Contactr integration
 * 
 * Universal list view for displaying contacts in a compact, scannable format.
 * Consumes Contact data from Contactr package.
 */
import React, { useState, useCallback } from 'react';
import type { Contact } from '@opcentral/contactr';
import './ContactListView.css';

export interface ContactListViewProps {
  /** Contact data to display */
  contacts: Contact[];
  /** Enable selection */
  selectable?: boolean;
  /** Selected contact IDs */
  selectedIds?: string[];
  /** Selection change handler */
  onSelectionChange?: (ids: string[]) => void;
  /** Contact click handler */
  onContactClick?: (contact: Contact) => void;
  /** Contact action handlers */
  onEmail?: (contact: Contact) => void;
  onCall?: (contact: Contact) => void;
  /** Show avatars */
  showAvatars?: boolean;
  /** Show company */
  showCompany?: boolean;
  /** Show tags */
  showTags?: boolean;
  /** Compact mode (smaller rows) */
  compact?: boolean;
  /** Custom row renderer */
  renderRow?: (contact: Contact) => React.ReactNode;
}

/**
 * ContactListView Component
 * 
 * Displays contacts in a list format with:
 * - Avatar (optional)
 * - Name and title
 * - Primary email and phone
 * - Company (optional)
 * - Tags (optional)
 * - Quick actions
 */
export function ContactListView({
  contacts,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onContactClick,
  onEmail,
  onCall,
  showAvatars = true,
  showCompany = true,
  showTags = false,
  compact = false,
  renderRow,
}: ContactListViewProps) {
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set(selectedIds));

  // Update selected set when prop changes
  React.useEffect(() => {
    setSelectedSet(new Set(selectedIds));
  }, [selectedIds]);

  // Handle selection toggle
  const handleToggleSelection = useCallback((contactId: string) => {
    if (!selectable) return;

    const newSet = new Set(selectedSet);
    if (newSet.has(contactId)) {
      newSet.delete(contactId);
    } else {
      newSet.add(contactId);
    }
    setSelectedSet(newSet);

    if (onSelectionChange) {
      onSelectionChange(Array.from(newSet));
    }
  }, [selectable, selectedSet, onSelectionChange]);

  // Handle contact click
  const handleContactClick = useCallback((contact: Contact) => {
    if (onContactClick) {
      onContactClick(contact);
    }
  }, [onContactClick]);

  // Get primary email
  const getPrimaryEmail = (contact: Contact): string | undefined => {
    const primary = contact.emails.find((e) => e.isPrimary);
    return primary?.email || contact.emails[0]?.email;
  };

  // Get primary phone
  const getPrimaryPhone = (contact: Contact): string | undefined => {
    const primary = contact.phones.find((p) => p.isPrimary);
    return primary?.number || contact.phones[0]?.number;
  };

  const listClass = `contact-list ${compact ? 'contact-list-compact' : ''}`;

  return (
    <div className="contact-list-view">
      <div className={listClass}>
        {contacts.map((contact) => {
          const isSelected = selectedSet.has(contact.id);
          const primaryEmail = getPrimaryEmail(contact);
          const primaryPhone = getPrimaryPhone(contact);

          // Use custom renderer if provided
          if (renderRow) {
            return (
              <div key={contact.id} className="contact-list-item-wrapper">
                {renderRow(contact)}
              </div>
            );
          }

          return (
            <div
              key={contact.id}
              className={`contact-list-item ${isSelected ? 'contact-list-item-selected' : ''}`}
              onClick={() => handleContactClick(contact)}
            >
              {/* Selection checkbox */}
              {selectable && (
                <div className="contact-list-select">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSelection(contact.id);
                    }}
                    className="contact-list-checkbox"
                  />
                </div>
              )}

              {/* Avatar */}
              {showAvatars && (
                <div className="contact-list-avatar">
                  {contact.avatarUrl ? (
                    <img src={contact.avatarUrl} alt={contact.displayName} />
                  ) : (
                    <div className="contact-list-avatar-placeholder">
                      {contact.displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              )}

              {/* Main content */}
              <div className="contact-list-content">
                <div className="contact-list-primary">
                  <span className="contact-list-name">{contact.displayName}</span>
                  {contact.jobTitle && (
                    <span className="contact-list-title"> • {contact.jobTitle}</span>
                  )}
                  {showCompany && contact.company && (
                    <span className="contact-list-company"> • {contact.company}</span>
                  )}
                </div>

                <div className="contact-list-secondary">
                  {primaryEmail && (
                    <span className="contact-list-detail">
                      <span className="contact-list-icon">✉️</span>
                      {primaryEmail}
                    </span>
                  )}
                  {primaryPhone && (
                    <span className="contact-list-detail">
                      <span className="contact-list-icon">📞</span>
                      {primaryPhone}
                    </span>
                  )}
                </div>

                {/* Tags */}
                {showTags && contact.tags.length > 0 && (
                  <div className="contact-list-tags">
                    {contact.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="contact-list-tag">
                        {tag}
                      </span>
                    ))}
                    {contact.tags.length > 3 && (
                      <span className="contact-list-tag contact-list-tag-more">
                        +{contact.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="contact-list-actions">
                {onEmail && primaryEmail && (
                  <button
                    className="contact-list-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEmail(contact);
                    }}
                    title="Send email"
                  >
                    ✉️
                  </button>
                )}
                {onCall && primaryPhone && (
                  <button
                    className="contact-list-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCall(contact);
                    }}
                    title="Call"
                  >
                    📞
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {contacts.length === 0 && (
        <div className="contact-list-empty">
          <p>No contacts to display</p>
        </div>
      )}
    </div>
  );
}

export default ContactListView;
